import { Box, Button, Group } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import api from "../../../api";
import { useDownload } from "../../../hooks/useDownload";
import { useProject } from "../../../hooks/useProject";
import { patchSiteCategories } from "../../../hooks/useSiteCategories";
import { useToast } from "../../../mantine/notifications/useToast";
import { SiteLicense } from "../../../types/api.types";
import urls from "../../../urls";
import type {
  Site,
  SiteCategoriesResponse,
  SiteCategoriesWithSiteFeatures
} from "../../../utils/backend-types";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { DeleteProjectWidget } from "./DeleteProjectWidget/DeleteProjectWidget";
import { ProjectSettingsForm } from "./ProjectSettingsForm/ProjectSettingsForm";
import { SiteCategoriesTable } from "./SiteCategoriesTable/SiteCategoriesTable";

interface SettingsPageProps {
  projectId: string;
  variantId: number;
  projectSites: ReadonlyArray<Site>;
  siteCategories: Array<SiteCategoriesResponse>;
}

function SettingsPage({
  projectId,
  variantId,
  projectSites,
  siteCategories
}: SettingsPageProps) {
  const { showToast, showErrorToast } = useToast();
  const { project } = useProject(projectId);
  const { shouldShowStaffView } = useShouldShowStaffView();
  const queryClient = useQueryClient();

  const [updatedSiteCategories, setUpdatedSiteCategories] = useState<
    Array<SiteCategoriesWithSiteFeatures>
  >([]);

  const sapExportInputEnabled = projectSites.some(
    (site) => site.invoicing_active
  );

  const buildSiteCategoriesWithInvoiceStatus = useCallback(() => {
    if (!siteCategories) return [];
    return siteCategories.map<SiteCategoriesWithSiteFeatures>(
      (siteCategory) => {
        const siteInvoiceStatus = projectSites.find(
          (site) => site.id === siteCategory.id
        );
        return {
          ...siteCategory,
          invoicing_active: siteInvoiceStatus?.invoicing_active ?? false,
          metering_concept_v2_active:
            siteInvoiceStatus?.metering_concept_v2_active ?? false,
          go_active: siteInvoiceStatus?.go_active ?? false,
          premium_todos_active: siteInvoiceStatus?.premium_todos_active ?? false
        };
      }
    );
  }, [projectSites, siteCategories]);

  useEffect(() => {
    setUpdatedSiteCategories(buildSiteCategoriesWithInvoiceStatus());
  }, [buildSiteCategoriesWithInvoiceStatus]);

  function handleClickSave() {
    showToast({
      type: "success",
      message: "Das Projekt wurde erfolgreich aktualisiert."
    });
    queryClient.invalidateQueries({
      queryKey: [
        "project",
        {
          projectId
        }
      ]
    });
    queryClient.invalidateQueries({
      queryKey: ["projects-list"]
    });

    if (shouldShowStaffView) {
      const updatedSiteCategoriesWithoutInvoicingActive =
        updatedSiteCategories?.map(({ invoicing_active, ...rest }) => rest);

      patchSiteCategories(
        showToast,
        showErrorToast,
        projectId,
        updatedSiteCategoriesWithoutInvoicingActive
      ).then(() => {
        queryClient.invalidateQueries({
          queryKey: ["siteCategories", { projectOrSiteId: projectId }]
        });
      });
    }
  }

  const { download, isLoading } = useDownload();

  async function handleChangeInvoiceStatus(
    id: number,
    invoicingActive: boolean
  ) {
    const url = urls.api.setSiteInvoicing(id, invoicingActive);
    const successMessage =
      "Die Rechnungsstellung wurde für diese Liegenschaft" +
      (invoicingActive ? " aktiviert." : " deaktiviert.");
    const oldUpdatedSiteCategories = [...updatedSiteCategories];
    const updatedSiteCategoriesWithInvoiceStatus = oldUpdatedSiteCategories.map(
      (siteCategory) => {
        if (siteCategory.id === id) {
          return {
            ...siteCategory,
            invoicing_active: invoicingActive
          };
        }
        return siteCategory;
      }
    );
    setUpdatedSiteCategories(updatedSiteCategoriesWithInvoiceStatus);
    try {
      await api.post(url);
      queryClient.invalidateQueries({
        queryKey: ["sites-list", { variantId }]
      });
      showToast({ type: "success", message: successMessage });
    } catch (error) {
      setUpdatedSiteCategories(oldUpdatedSiteCategories);
      showErrorToast(error);
    }
  }

  async function handleChangeMeteringConceptStatus(
    id: number,
    active: boolean
  ) {
    const url = active
      ? urls.api.activateMeteringConceptV2(id)
      : urls.api.deactivateMeteringConceptV2(id);
    const successMessage =
      "Die Verwendung des MBK V2 für Rechnungsstellung wurde für diese Liegenschaft" +
      (active ? " aktiviert." : " deaktiviert.");
    const oldUpdatedSiteCategories = [...updatedSiteCategories];
    const updatedSiteCategoriesWithMBKV2Status = oldUpdatedSiteCategories.map(
      (siteCategory) => {
        if (siteCategory.id === id) {
          return {
            ...siteCategory,
            metering_concept_v2_active: active
          };
        }
        return siteCategory;
      }
    );
    setUpdatedSiteCategories(updatedSiteCategoriesWithMBKV2Status);
    try {
      await api.post(url);
      queryClient.invalidateQueries({
        queryKey: ["sites-list", { variantId }]
      });
      showToast({ type: "success", message: successMessage });
    } catch (error) {
      setUpdatedSiteCategories(oldUpdatedSiteCategories);
      showErrorToast(error);
    }
  }

  async function handleChangeGoStatus(id: number, goActive: boolean) {
    const url = urls.api.setSiteGo(id, goActive);
    const successMessage =
      "Die HKN Dokumentation wurde für diese Liegenschaft" +
      (goActive ? " aktiviert." : " deaktiviert.");
    const oldUpdatedSiteCategories = [...updatedSiteCategories];
    const updatedSiteCategoriesWithGOStatus = oldUpdatedSiteCategories.map(
      (siteCategory) => {
        if (siteCategory.id === id) {
          return {
            ...siteCategory,
            go_active: goActive
          };
        }
        return siteCategory;
      }
    );
    setUpdatedSiteCategories(updatedSiteCategoriesWithGOStatus);
    try {
      await api.post(url);
      queryClient.invalidateQueries({
        queryKey: ["sites-list", { variantId }]
      });
      showToast({ type: "success", message: successMessage });
    } catch (error) {
      setUpdatedSiteCategories(oldUpdatedSiteCategories);
      showErrorToast(error);
    }
  }

  async function handleChangeLicense(id: number, newLicense: SiteLicense) {
    const url = urls.api.setSiteLicense(id, newLicense);
    const newLicenseName =
      newLicense === SiteLicense.premium
        ? "Premium"
        : newLicense === SiteLicense.light
          ? "Light"
          : "Basis";
    const successMessage = `Für diese Liegenschaft wurde eine ${newLicenseName}-Lizenz hinterlegt.`;
    const oldUpdatedSiteCategories = [...updatedSiteCategories];
    const updatedSiteCategoriesWithPremium = oldUpdatedSiteCategories.map(
      (siteCategory) => {
        if (siteCategory.id === id) {
          return {
            ...siteCategory,
            license: newLicense
          };
        }
        return siteCategory;
      }
    );
    setUpdatedSiteCategories(updatedSiteCategoriesWithPremium);

    try {
      await api.post(url);
      queryClient.invalidateQueries({
        queryKey: ["sites-list", { variantId }]
      });
      queryClient.invalidateQueries({
        queryKey: ["siteCategories", { projectOrSiteId: projectId }]
      });
      showToast({ type: "success", message: successMessage });
    } catch (error) {
      setUpdatedSiteCategories(oldUpdatedSiteCategories);
      showErrorToast(error);
    }
  }

  async function handleChangePremiumTodosStatus(
    id: number,
    premiumTodosActive: boolean
  ) {
    const url = urls.api.setSitePremiumTodos(id, premiumTodosActive);
    const successMessage =
      "Premium-Aufgaben wurden für diese Liegenschaft" +
      (premiumTodosActive ? " aktiviert." : " deaktiviert.");
    const oldUpdatedSiteCategories = [...updatedSiteCategories];
    try {
      await api.post(url);
      if (updatedSiteCategories) {
        const updatedSiteCategoriesWithInvoiceStatus =
          updatedSiteCategories.map((siteCategory) => {
            if (siteCategory.id === id) {
              return {
                ...siteCategory,
                premium_todos_active: premiumTodosActive
              };
            }
            return siteCategory;
          });
        setUpdatedSiteCategories(updatedSiteCategoriesWithInvoiceStatus);
        queryClient.invalidateQueries({
          queryKey: ["sites-list", { variantId }]
        });
        showToast({ type: "success", message: successMessage });
      }
    } catch (error) {
      setUpdatedSiteCategories(oldUpdatedSiteCategories);
      showErrorToast(error);
    }
  }

  return (
    <div className="SettingsPage">
      {shouldShowStaffView && updatedSiteCategories && (
        <Box mb="lg">
          <SiteCategoriesTable
            data={updatedSiteCategories}
            onChange={setUpdatedSiteCategories}
            onChangeGoStatus={handleChangeGoStatus}
            onChangeInvoiceStatus={handleChangeInvoiceStatus}
            onChangeLicense={handleChangeLicense}
            onChangeMeteringConceptStatus={handleChangeMeteringConceptStatus}
            onChangePremiumTodosStatus={handleChangePremiumTodosStatus}
          />
        </Box>
      )}
      {!!project && (
        <Box maw={450} mb="lg">
          <ProjectSettingsForm
            project={project}
            sapExportInputEnabled={sapExportInputEnabled}
            showStaffView={shouldShowStaffView}
            onSubmit={handleClickSave}
          />
        </Box>
      )}
      <Group gap="xs">
        <Button
          color="primary"
          loading={isLoading}
          onClick={() =>
            download({
              downloadUrl: urls.api.exportProjectAsExcel(variantId)
            })
          }
        >
          Projekt als .xlsx exportieren
        </Button>
        <DeleteProjectWidget
          project={project}
          showStaffView={shouldShowStaffView}
        />
      </Group>
    </div>
  );
}

export { SettingsPage };
